import {
    useFocusEffect,
    useNavigation
} from '@react-navigation/native';
import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { useSelector } from 'react-redux';

import {
    Button,
    Card,
    Icon,
    Text,
    View
} from '../../../common';
import {
    inAppUpdaterStyles,
    inAppUpdaterStyles as styles
} from '../../../common/InAppUpdater';
import {
    getIsAuthenticated,
    getIsPersonalized
} from '../../../selectors';
import {
    dbGettr,
    prefGettr
} from '../../../selectors/utils';
import { storage } from '../../../services/localstorage';
import { getGoogleDriveConnectionInfo } from '../../Services/Backup/google/googleDrive';

export const CLOUD_CONNECT_REMINDER_STORAGE_KEY = 'cloudConnectReminderClosed';

// eslint-disable-next-line no-console,no-unused-vars
const toLog = (message, ...data) => {
    // Disabled for production, uncomment for debugging
    // console.log(message, ...data);
};

export const CloudConnectReminder = () => {
    const [ show, setShow ] = React.useState(false);
    const googleDriveConnectReminderTime = useSelector(dbGettr('googleDriveConnectReminderTime'));
    const googleDriveIosBackup = useSelector(dbGettr('googleDriveIosBackup'));
    const googleDriveIosRestore = useSelector(dbGettr('googleDriveIosRestore'));
    const googleCloudEnabled = useSelector(prefGettr('googleCloudEnabled', false));
    const iCloudEnabled = useSelector(prefGettr('iCloudEnabled', false));
    const navigation = useNavigation();
    const isLogged = useSelector(getIsAuthenticated);
    const isProfile = useSelector(getIsPersonalized);

    const checkLastTimeNotificationClosed = useCallback(async () => {
        const lastTimeClosed = await storage.get(CLOUD_CONNECT_REMINDER_STORAGE_KEY);
        
        // Check cloud connections based on platform and settings
        let isAnyCloudConnected = false;
        
        if (Platform.OS === 'android') {
            // For Android, only check Google Drive
            const googleDriveConnections = await getGoogleDriveConnectionInfo();
            isAnyCloudConnected = googleDriveConnections?.type === 'success';
            toLog('CloudConnectReminder: Android - Google Drive connection', googleDriveConnections, lastTimeClosed);
        } else if (Platform.OS === 'ios') {
            // For iOS, check both Google Drive and iCloud based on settings
            const shouldCheckGoogleDrive = googleDriveIosRestore || googleDriveIosBackup;
            
            if (shouldCheckGoogleDrive) {
                // Check Google Drive if it's enabled for iOS
                const googleDriveConnections = await getGoogleDriveConnectionInfo();
                const isGoogleDriveConnected = googleDriveConnections?.type === 'success' && googleCloudEnabled;
                
                // For iOS with Google Drive enabled, show reminder if NEITHER is connected
                isAnyCloudConnected = isGoogleDriveConnected || iCloudEnabled;
            } else {
                isAnyCloudConnected = iCloudEnabled;
            }
        }

        if (isAnyCloudConnected) {
            setShow(false);
            return;
        }
        
        if (!lastTimeClosed) {
            setShow(true);
            return;
        }
        
        const now = Date.now();
        const lastTime = lastTimeClosed + googleDriveConnectReminderTime * 1000;
        let res = false;
        if (lastTime < now) {
            res = true;
        }
        setShow(res);
    }, [
        googleDriveConnectReminderTime,
        googleDriveIosRestore,
        googleDriveIosBackup, 
        iCloudEnabled, 
        googleCloudEnabled
    ]);

    useFocusEffect(
        useCallback(() => {
            checkLastTimeNotificationClosed();
        }, [ googleDriveConnectReminderTime,
            setShow,
            checkLastTimeNotificationClosed ]),
    );

    const closeWindowHandler = useCallback(async () => {
        storage.set(CLOUD_CONNECT_REMINDER_STORAGE_KEY, Date.now())
            .finally(() => {
                checkLastTimeNotificationClosed();
            });
    }, [checkLastTimeNotificationClosed]);

    if (Platform.OS === 'web') {
        toLog('CloudConnectReminder: Web platform detected, skipping reminder');
        return null;
    }
    return (Platform.OS === 'android' || (Platform.OS === 'ios')) &&
    show &&
    isLogged && isProfile === true ? (
            <Card containerStyle={[ styles.wrapper, { position: 'relative' } ]}>
                <View style={styles.lineWrapper}>
                    <Button
                        action={closeWindowHandler}
                        styles={[
                            styles.closeButton,
                            {
                                position: 'absolute',
                                right: -14,
                                top: -14,
                            },
                        ]}
                    >
                        <Icon
                            name="cross"
                            color="#787878"
                            style={styles.closeIcon}
                        />
                    </Button>
                </View>
                <Text style={inAppUpdaterStyles.stdText}>{Object.R('reminder.connectGoogleDrive')}</Text>
                <Button
                    transparent
                    title={Object.R('reminder.connectGoogleDriveButton')}
                    titleStyles={{ color: '#5C85DD' }}
                    action={() => {
                        navigation.navigate('CloudConnectSettings');
                    }}
                />
            </Card>
        ) : null;
};

